import React, { Component } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTime } from 'luxon';
import Loading from '../Common/Loading';
import DataArchiveGrid from './DataArchiveGrid';
import StationInfo from '../Common/StationInfo';
import PresetButton from '../Common/PresetButton';
import './Reports.css';

const stateDefaults = {
  beginDate: DateTime.local().minus({ days: 1 }),
  endDate: DateTime.local().minus({ days: 1 }),
  dateRangeError: null,
  loading: false,
  data: null,
  selectedPreset: 'Yesterday'
};

export default class Reports extends Component {
  state = {
    contentRef: React.createRef(),
    contentWidth: null,
    contentHeight: null,
    ...stateDefaults
  };

  ro = null;

  componentDidMount() {
    this.ro = new ResizeObserver(this.onResize);

    this.ro.observe(this.state.contentRef.current);

    this.fetchDataArchiveReport(this.state.beginDate, this.state.endDate);
  }

  componentWillUnmount() {
    this.ro.unobserve(this.state.contentRef.current);
  }

  onResize = (entries, observer) => {
    this.setState({
      contentWidth:
        entries[0].contentRect.width > 1420
          ? 1420
          : entries[0].contentRect.width,
      contentHeight: entries[0].contentRect.height
    });
  };


  fetchDataArchiveReport(begin, end) {
    this.setState({ loading: true, data: null });

    fetch(
      `${process.env.REACT_APP_ARCHIVE_WITHIN_RANGE}&begin=${begin
        .startOf('day')
        .toMillis() / 1000}&end=${end.endOf('day').toMillis() /
        1000}&sortDirection=ASC`
    ).then(response => {
      response.json().then(json => {
        this.setState({ loading: false, data: json });
      });
    });
  }

  validateInput() {
    const daysDifference = this.state.endDate.diff(
      this.state.beginDate,
      'days'
    ).days;

    if (daysDifference > 8) {
      this.setState({
        dateRangeError: 'The date range must be a week or less.',
        data: stateDefaults.data
      });
    } else if (daysDifference < 0) {
      this.setState({
        dateRangeError: 'The begin date must be before the end date.',
        data: stateDefaults.data
      });
    } else {
      this.setState({ dateRangeError: null });

      this.fetchDataArchiveReport(this.state.beginDate, this.state.endDate);
    }
  }

  onPresetClick = preset => {
    let beginDate, endDate;

    switch (preset) {
      case 'Today':
        beginDate = DateTime.local();
        endDate = DateTime.local();
        break;
      case 'Yesterday':
        beginDate = DateTime.local().minus({ days: 1 });
        endDate = DateTime.local().minus({ days: 1 });
        break;
      case '2 Days Ago':
        beginDate = DateTime.local().minus({ days: 2 });
        endDate = DateTime.local().minus({ days: 2 });
        break;
      case '3 Days Ago':
        beginDate = DateTime.local().minus({ days: 3 });
        endDate = DateTime.local().minus({ days: 3 });
        break;
      case '4 Days Ago':
        beginDate = DateTime.local().minus({ days: 4 });
        endDate = DateTime.local().minus({ days: 4 });
        break;
      case '5 Days Ago':
        beginDate = DateTime.local().minus({ days: 5 });
        endDate = DateTime.local().minus({ days: 5 });
        break;
      default:
        return;
    }

    this.setState({
      beginDate,
      endDate,
      selectedPreset: preset
    }, () => {
      this.validateInput();
    });
  };

  isPresetSelected = preset => {
    return this.state.selectedPreset === preset;
  };

  handleBeginDateChange(date) {
    this.setState({ beginDate: date, selectedPreset: null }, this.validateInput);
  }

  handleEndDateChange(date) {
    this.setState({ endDate: date, selectedPreset: null }, this.validateInput);
  }

  render() {
    return (
      <div id="Reports">
        <StationInfo
          name={process.env.REACT_APP_STATION_NAME}
          latitude={process.env.REACT_APP_LATITUDE}
          longitude={process.env.REACT_APP_LONGITUDE}
          elevation={process.env.REACT_APP_ELEVATION}
        />
        <div id="date-selection">
          <div id="date-range">
            <div className="presets">
              <PresetButton
                selected={this.isPresetSelected('Today')}
                onClick={() => this.onPresetClick('Today')}
              >
                Today
              </PresetButton>
              <PresetButton
                selected={this.isPresetSelected('2 Days Ago')}
                onClick={() => this.onPresetClick('2 Days Ago')}
              >
                2 Days Ago
              </PresetButton>
              <PresetButton
                selected={this.isPresetSelected('4 Days Ago')}
                onClick={() => this.onPresetClick('4 Days Ago')}
              >
                4 Days Ago
              </PresetButton>
              <PresetButton
                selected={this.isPresetSelected('Yesterday')}
                onClick={() => this.onPresetClick('Yesterday')}
              >
                Yesterday
              </PresetButton>
              <PresetButton
                selected={this.isPresetSelected('3 Days Ago')}
                onClick={() => this.onPresetClick('3 Days Ago')}
              >
                3 Days Ago
              </PresetButton>
              <PresetButton
                selected={this.isPresetSelected('5 Days Ago')}
                onClick={() => this.onPresetClick('5 Days Ago')}
              >
                5 Days Ago
              </PresetButton>
            </div>
            <div className="title">Date Range</div>
            <div className="range">
              <DatePicker
                allowKeyboardControl={false}
                className="begin"
                leftArrowIcon="<"
                rightArrowIcon=">"
                autoOk={true}
                minDate={DateTime.local(2018, 1, 1).startOf('year')}
                maxDate={DateTime.local()}
                format={'LL/dd/yyyy'}
                value={this.state.beginDate}
                onChange={date => this.handleBeginDateChange(date)}
              />
              <DatePicker
                allowKeyboardControl={false}
                className="end"
                leftArrowIcon="<"
                rightArrowIcon=">"
                autoOk={true}
                minDate={DateTime.local(2018, 1, 1).startOf('year')}
                maxDate={DateTime.local()}
                format={'LL/dd/yyyy'}
                value={this.state.endDate}
                onChange={date => this.handleEndDateChange(date)}
              />
            </div>
            {this.state.dateRangeError && (
              <div className="error">{this.state.dateRangeError}</div>
            )}
          </div>
        </div>
        <div id="content" ref={this.state.contentRef}>
          {!this.state.loading && (!Array.isArray(this.state.data) || this.state.data.length === 0) &&
            process.env.REACT_APP_STATION_NAME.includes("Wildrose") && (
              <div className="data-availability-notice">
                Data is only available beginning X date.
              </div>
            )}
          {!this.state.loading && (!Array.isArray(this.state.data) || this.state.data.length === 0) &&
            process.env.REACT_APP_STATION_NAME.includes("Pelion") && (
              <div className="data-availability-notice">
                Data is only available July 7, 2025 through May 24, 2026.
              </div>
            )}
          {!this.state.loading && (!Array.isArray(this.state.data) || this.state.data.length === 0) &&
            process.env.REACT_APP_STATION_NAME.includes("Castle Pines") && (
              <div className="data-availability-notice">
                Data is only available through June 9, 2025.
              </div>
            )}
          {this.state.loading && <Loading />}
          {!this.state.loading && this.state.data && this.state.data.length > 0 && (
            <DataArchiveGrid
              id="data-archive-grid"
              data={this.state.data}
              width={this.state.contentWidth}
              height={this.state.contentHeight}
            />
          )}
        </div>
      </div>
    );
  }
}
